import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { PageContainer, PageToolbar, ToolbarDivider } from '../../components/ui/PageLayout';
import {
  AnimatedTabIndicator,
  animatedTabTriggerClass,
} from '../../components/ui/AnimatedTabIndicator';
import toast from '../../services/toast';
import {
  GitFork,
  Loader2,
  FolderKanban,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Info,
  X,
} from 'lucide-react';
import { PageToolbarSkeleton, TableSkeleton } from '../../components/ui/Skeleton';
import SearchBar from '../../components/ui/SearchBar';
import { useOrganisationName } from '../../hooks/useOrganisationName';
import { useResolvedOrgId } from '../../hooks/useResolvedOrgId';
import { buildRepositoriesDiscoveredColumns } from './repositoriesTableColumns';
import RepositoriesConnectedTable from '../../components/repositories/RepositoriesConnectedTable';
import AssignRepositoryModal from './AssignRepositoryModal';
import { listRowClass } from '../../components/repository/repoViewTheme';
import { table, inputCompact, cn } from '../../components/ui/uiTheme';
import { useRepositoriesPageQueries } from './useRepositoriesPageQueries';

const Repositories = () => {
  const navigate = useNavigate();
  const orgName = useOrganisationName();
  const { orgId, ready } = useResolvedOrgId();
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningRepo, setAssigningRepo] = useState<any>(null);
  const [selectedStreamId, setSelectedStreamId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [searchConnected, setSearchConnected] = useState('');
  const [searchDiscovered, setSearchDiscovered] = useState('');
  const [debouncedSearchConnected, setDebouncedSearchConnected] = useState('');
  const [debouncedSearchDiscovered, setDebouncedSearchDiscovered] = useState('');
  const [sortingDiscovered, setSortingDiscovered] = useState([{ id: 'fullName', desc: false }]);
  const [discoveredPageInput, setDiscoveredPageInput] = useState('');
  const [activeTab, setActiveTab] = useState('connected');
  const [repoHintDismissed, setRepoHintDismissed] = useState(() => {
    try {
      return localStorage.getItem('vp_repo_connect_hint_dismissed') === '1';
    } catch {
      return false;
    }
  });

  const {
    org,
    repositories,
    discoveredRepositories,
    streams,
    streamProjects,
    loading,
    loadingDiscovered,
    loadingStreamProjects,
    refreshDiscoveredMutation,
    connectRepositoryMutation,
    profileError,
    discoveredError,
    isRefreshingDiscovered,
  } = useRepositoriesPageQueries({
    orgId,
    ready,
    debouncedSearchConnected,
    debouncedSearchDiscovered,
    assignModalOpen,
    selectedStreamId,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchConnected(searchConnected), 300);
    return () => clearTimeout(t);
  }, [searchConnected]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchDiscovered(searchDiscovered), 300);
    return () => clearTimeout(t);
  }, [searchDiscovered]);

  useEffect(() => {
    if (profileError) {
      toast.error(profileError.message || 'Failed to load');
    }
  }, [profileError]);

  useEffect(() => {
    if (discoveredError) {
      toast.error(discoveredError.message || 'Failed to load discovered repos');
    }
  }, [discoveredError]);

  useEffect(() => {
    if (!assignModalOpen || !selectedStreamId) {
      setSelectedProjectId('');
      return;
    }
    if (streamProjects.length > 0) {
      setSelectedProjectId((prev) =>
        streamProjects.some((p) => p.id === prev) ? prev : streamProjects[0]?.id || ''
      );
    } else {
      setSelectedProjectId('');
    }
  }, [assignModalOpen, selectedStreamId, streamProjects]);

  const refreshDiscovered = useCallback(() => {
    if (!orgId) return;
    refreshDiscoveredMutation.mutate();
  }, [orgId, refreshDiscoveredMutation]);

  const handleOpenAssign = useCallback((repo) => {
    setAssigningRepo(repo);
    setSelectedStreamId('');
    setSelectedProjectId('');
    setAssignModalOpen(true);
  }, []);

  const handleAssignConfirm = async () => {
    if (!orgId || !assigningRepo || !selectedProjectId) return;
    setAssignSaving(true);
    try {
      await connectRepositoryMutation.mutateAsync({
        projectId: selectedProjectId,
        repository: assigningRepo.fullName,
        deploymentBranches: [assigningRepo.defaultBranch || 'main'],
      });
      setAssignModalOpen(false);
      setAssigningRepo(null);
    } finally {
      setAssignSaving(false);
    }
  };

  const discoveredColumns = useMemo(
    () => buildRepositoriesDiscoveredColumns(handleOpenAssign),
    [handleOpenAssign]
  );

  const discoveredTable = useReactTable({
    data: discoveredRepositories,
    columns: discoveredColumns,
    getRowId: (row) => row.id ?? `gh-${(row as any).gitHubId ?? ''}-${row.fullName ?? ''}`,
    state: { sorting: sortingDiscovered },
    onSortingChange: setSortingDiscovered,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 15 } },
  });

  useEffect(() => {
    discoveredTable.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveredRepositories.length, debouncedSearchDiscovered]);

  const hasGitHubIntegration = org?.hasToken === true || org?.hasGitHubIntegration === true;

  const renderSortHeader = (header) => (
    <div
      className={`flex items-center justify-between group ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
      onClick={header.column.getToggleSortingHandler()}
      onKeyDown={
        header.column.getCanSort()
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                header.column.getToggleSortingHandler()?.(e);
              }
            }
          : undefined
      }
      role={header.column.getCanSort() ? 'button' : undefined}
      tabIndex={header.column.getCanSort() ? 0 : undefined}
      aria-sort={
        header.column.getIsSorted() === 'asc'
          ? 'ascending'
          : header.column.getIsSorted() === 'desc'
            ? 'descending'
            : header.column.getCanSort()
              ? 'none'
              : undefined
      }
    >
      <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
      {header.column.getCanSort() && (
        <span className="ml-2 opacity-60 group-hover:opacity-100 transition-opacity" aria-hidden>
          {header.column.getIsSorted() === 'asc' && <ArrowUp className="w-4 h-4 text-vivid-red" />}
          {header.column.getIsSorted() === 'desc' && (
            <ArrowDown className="w-4 h-4 text-vivid-red" />
          )}
          {!header.column.getIsSorted() && <ArrowUpDown className="w-4 h-4 text-slate-500" />}
        </span>
      )}
    </div>
  );

  const renderPagination = (tableInst, pageInput, setPageInput, idPrefix) =>
    tableInst.getPageCount() > 1 ? (
      <div className="bg-dark-surface px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-dark-border sm:px-6">
        <p className="text-sm text-slate-300">
          Showing{' '}
          <span className="font-medium">
            {tableInst.getState().pagination.pageIndex * tableInst.getState().pagination.pageSize +
              1}
          </span>{' '}
          to{' '}
          <span className="font-medium">
            {Math.min(
              (tableInst.getState().pagination.pageIndex + 1) *
                tableInst.getState().pagination.pageSize,
              tableInst.getPrePaginationRowModel().rows.length
            )}
          </span>{' '}
          of <span className="font-medium">{tableInst.getPrePaginationRowModel().rows.length}</span>
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-300">Page</span>
            <input
              type="number"
              min="1"
              max={tableInst.getPageCount()}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const page = (e.target as HTMLInputElement).value
                    ? Number((e.target as HTMLInputElement).value) - 1
                    : 0;
                  if (page >= 0 && page < tableInst.getPageCount()) {
                    tableInst.setPageIndex(page);
                    setPageInput('');
                  }
                }
              }}
              placeholder={`${tableInst.getState().pagination.pageIndex + 1}`}
              className={cn(inputCompact, 'w-16 text-center py-1')}
              aria-label="Go to page"
              id={`${idPrefix}-page-input`}
            />
            <span className="text-sm text-slate-500">of {tableInst.getPageCount()}</span>
          </div>
          <nav className="inline-flex rounded-md -space-x-px" aria-label="Pagination">
            <button
              type="button"
              onClick={() => tableInst.previousPage()}
              disabled={!tableInst.getCanPreviousPage()}
              className={cn(
                'btn-secondary-mdl-sm relative inline-flex items-center rounded-l-md disabled:opacity-50'
              )}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => tableInst.nextPage()}
              disabled={!tableInst.getCanNextPage()}
              className={cn(
                'btn-secondary-mdl-sm relative inline-flex items-center rounded-r-md disabled:opacity-50'
              )}
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    ) : null;

  if (loading) {
    return (
      <PageContainer>
        <PageToolbarSkeleton />
        <div className="border-t border-dark-border my-4" />
        <section className="mb-8">
          <TableSkeleton cols={5} rows={6} />
        </section>
      </PageContainer>
    );
  }

  if (!orgId) {
    return (
      <PageContainer>
        <p className="text-slate-400">No organisation found. Please complete setup.</p>
      </PageContainer>
    );
  }

  const orgLabel = orgName || 'Organisation';
  const hasConnectedSearch = Boolean(
    debouncedSearchConnected && String(debouncedSearchConnected).trim()
  );
  const SEARCH_PLACEHOLDER = 'Search Repositories';

  return (
    <PageContainer>
      <PageToolbar
        title="Repositories"
        breadcrumbItems={[{ label: orgLabel, to: '/' }, { label: 'Repositories' }]}
      />
      <ToolbarDivider />

      {!repoHintDismissed ? (
        <div className="mb-4 flex gap-3 rounded-xl border-blue-800 bg-blue-900/20 p-4 text-blue-100">
          <Info className="h-5 w-5 shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0 flex-1">
            Repositories must be connected to a project to appear in project metrics. On the
            Discovered tab, use <strong>Connect to project</strong> on each row (or open a project
            and add repositories from there).
          </div>
          <button
            type="button"
            className="shrink-0 rounded-lg p-1 hover:bg-blue-800/40"
            aria-label="Dismiss"
            onClick={() => {
              try {
                localStorage.setItem('vp_repo_connect_hint_dismissed', '1');
              } catch {
                /* ignore */
              }
              setRepoHintDismissed(true);
            }}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}

      <div className="border-b border-dark-border mb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4 min-w-0">
          <nav
            className="flex flex-wrap gap-1 -mb-px shrink-0"
            role="tablist"
            aria-label="Repository lists"
          >
            <button
              type="button"
              role="tab"
              id="repos-tab-connected"
              aria-selected={activeTab === 'connected'}
              aria-controls="repos-tabpanel-connected"
              tabIndex={activeTab === 'connected' ? 0 : -1}
              onClick={() => setActiveTab('connected')}
              className={animatedTabTriggerClass(activeTab === 'connected')}
            >
              <AnimatedTabIndicator
                layoutId="repos-list-tab-indicator"
                isActive={activeTab === 'connected'}
              />
              <span className="relative z-10 inline-flex items-center gap-2">
                <GitFork className="h-4 w-4 shrink-0" aria-hidden />
                Connected
              </span>
            </button>
            <button
              type="button"
              role="tab"
              id="repos-tab-discovered"
              aria-selected={activeTab === 'discovered'}
              aria-controls="repos-tabpanel-discovered"
              tabIndex={activeTab === 'discovered' ? 0 : -1}
              onClick={() => setActiveTab('discovered')}
              className={animatedTabTriggerClass(activeTab === 'discovered')}
            >
              <AnimatedTabIndicator
                layoutId="repos-list-tab-indicator"
                isActive={activeTab === 'discovered'}
              />
              <span className="relative z-10 inline-flex items-center gap-2">
                <FolderKanban className="h-4 w-4 shrink-0" aria-hidden />
                Discovered
              </span>
            </button>
          </nav>
          <div className="flex flex-wrap items-center justify-end gap-3 w-full min-w-0 sm:w-auto sm:flex-1 sm:max-w-none pb-px">
            {activeTab === 'connected' ? (
              <div className="w-full max-w-md sm:w-64 sm:max-w-[50vw] sm:shrink-0 sm:ml-auto">
                <SearchBar
                  variant="compact"
                  value={searchConnected}
                  onChange={setSearchConnected}
                  placeholder={SEARCH_PLACEHOLDER}
                />
              </div>
            ) : null}
            {activeTab === 'discovered' && hasGitHubIntegration ? (
              <>
                <div className="w-full max-w-md sm:w-64 sm:max-w-[50vw] sm:shrink-0 sm:ml-auto">
                  <SearchBar
                    variant="compact"
                    value={searchDiscovered}
                    onChange={setSearchDiscovered}
                    placeholder={SEARCH_PLACEHOLDER}
                  />
                </div>
                <button
                  type="button"
                  onClick={refreshDiscovered}
                  disabled={isRefreshingDiscovered}
                  className="btn-secondary-mdl-sm inline-flex h-9 min-h-[2.25rem] items-center gap-2 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isRefreshingDiscovered ? 'animate-spin' : ''}`}
                  />
                  Refresh
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div
        id="repos-tabpanel-connected"
        role="tabpanel"
        aria-labelledby="repos-tab-connected"
        hidden={activeTab !== 'connected'}
        className={activeTab !== 'connected' ? 'hidden' : undefined}
      >
        <RepositoriesConnectedTable
          repositories={repositories}
          navigate={navigate}
          showSearch={false}
          search={debouncedSearchConnected}
          emptyIcon={<GitFork className="w-12 h-12 text-slate-500" />}
          emptyTitle={
            hasConnectedSearch
              ? 'No repositories match your search.'
              : 'No repositories connected yet.'
          }
          emptyDescription={
            hasConnectedSearch
              ? 'Try a different search term.'
              : 'Connect a repository from a stream project, or open the Discovered tab to link an unassigned repo.'
          }
          emptyAction={
            !hasConnectedSearch ? (
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  type="button"
                  onClick={() => navigate('/streams')}
                  className="btn-primary-mdl"
                >
                  Go to Streams
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('discovered')}
                  className="btn-secondary-mdl"
                >
                  Open Discovered
                </button>
              </div>
            ) : undefined
          }
          paginationIdPrefix="repos-connected"
        />
      </div>

      <div
        id="repos-tabpanel-discovered"
        role="tabpanel"
        aria-labelledby="repos-tab-discovered"
        hidden={activeTab !== 'discovered'}
        className={activeTab !== 'discovered' ? 'hidden' : undefined}
      >
        {!hasGitHubIntegration ? (
          <p className="text-sm text-slate-400">
            Configure GitHub in organisation settings to see discovered repos.
          </p>
        ) : loadingDiscovered && discoveredRepositories.length === 0 ? (
          <div className="flex items-center gap-2 py-4 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading discovered repositories…</span>
          </div>
        ) : discoveredRepositories.length === 0 ? (
          <p className="text-sm text-slate-400">
            No unassigned repos. All organisation repos are already connected, or the list is empty.
          </p>
        ) : (
          <div className={cn(table.wrapper, 'overflow-hidden')}>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className={table.thead}>
                  {discoveredTable.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th key={header.id} scope="col" className={cn(table.th, 'text-left')}>
                          {renderSortHeader(header)}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-dark-surface">
                  {discoveredTable.getRowModel().rows.map((row) => (
                    <tr key={row.id} className={listRowClass}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={cn(table.td, 'align-middle')}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderPagination(
              discoveredTable,
              discoveredPageInput,
              setDiscoveredPageInput,
              'repos-discovered'
            )}
          </div>
        )}
      </div>

      <AssignRepositoryModal
        isOpen={assignModalOpen}
        onClose={() => {
          if (!assignSaving) {
            setAssignModalOpen(false);
            setAssigningRepo(null);
          }
        }}
        assignSaving={assignSaving}
        assigningRepo={assigningRepo}
        selectedStreamId={selectedStreamId}
        onSelectedStreamIdChange={setSelectedStreamId}
        selectedProjectId={selectedProjectId}
        onSelectedProjectIdChange={setSelectedProjectId}
        streams={streams}
        streamProjects={streamProjects}
        loadingStreamProjects={loadingStreamProjects}
        onConfirm={handleAssignConfirm}
      />
    </PageContainer>
  );
};

export default Repositories;
